import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Box,
  VStack,
  Heading,
  SimpleGrid,
  Flex,
  IconButton,
  Text,
  List,
  Separator,
} from '@chakra-ui/react';
import { FaPlus, FaTrash, FaCheckCircle } from 'react-icons/fa';
import { CustomInput } from '../../custom/CustomInput';
import { CustomSelect } from '../../custom/CustomSelect';
import { CustomButton } from '../../custom/CustomButton';

const DocumentsVerification = ({ onNext, onBack, loading, initialValues }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: initialValues || {
      documents: [
        {
          documentType: '',
          documentNo: '',
          expiryDate: '',
          file: null, 
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  // Watch documents to update UI when files are selected
  const watchedDocuments = watch('documents');

  const requiredDocuments = [
    'Certificate of Incorporation',
    'Activity License (if required)',
    'Memorandum & Articles of Association',
    'Company Address Proof (Bill, Bank Statement, etc.)',
    'Director’s & Shareholder’s Register',
    'Proof of ID and Address of Directors',
    'Proof of ID and Address of Shareholders',
    'Proof of ID and Address of Authorized Trader',
    'Company’s Most Recent Bank Statement',
    'Company’s Last Audited Financial Report',
    'Authority Letter for Authorized Trader',
    'Proof of Identity/Address for Beneficial Owners (>25%)',
  ];

  const onSubmit = (data) => {
    // Transform file input (FileList) to File object if present
    const transformedDocuments = data.documents.map(doc => {
        let file = doc.file;
        // Check if it's a FileList (from input type="file") and take the first file
        if (file && file.length > 0 && file[0] instanceof File) {
            file = file[0];
        }
        return { ...doc, file };
    });

    onNext({ ...data, documents: transformedDocuments });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={8} align="stretch">
        <Heading size="md" color="blue.600">
          Documents Verification
        </Heading>
        
        <Box bg="blue.50" p={6} borderRadius="md">
          <Heading size="sm" mb={4}>Required Documents Checklist:</Heading>
          <List.Root spacing={2} variant="plain">
            {requiredDocuments.map((doc, index) => (
              <List.Item key={index} display="flex" alignItems="center">
                <List.Indicator as={FaCheckCircle} color="blue.500" />
                <Text fontSize="sm">{doc}</Text>
              </List.Item>
            ))}
          </List.Root>
        </Box>

        <Separator />

        <Heading size="sm">Attach Documents</Heading>

        {fields.map((field, index) => {
          // Get the current value of the file field (from watch or default)
          const currentFile = watchedDocuments?.[index]?.file;
          const hasFile = currentFile && (
              (currentFile.length > 0) || // FileList
              (currentFile instanceof File) || // File object
              (currentFile.name) // Mock object
          );
          
          let fileName = "";
          if (currentFile) {
              if (currentFile instanceof File) fileName = currentFile.name;
              else if (currentFile.length > 0 && currentFile[0] instanceof File) fileName = currentFile[0].name;
              else if (currentFile.name) fileName = currentFile.name;
          }

          return (
          <Box
            key={field.id}
            p={6}
            borderWidth="1px"
            borderRadius="md"
            position="relative"
            bg="gray.50"
          >
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm">Document #{index + 1}</Heading>
                {fields.length > 1 && (
                    <IconButton
                        colorPalette="red"
                        variant="ghost"
                        onClick={() => remove(index)}
                        aria-label="Remove document"
                    >
                        <FaTrash />
                    </IconButton>
                )}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} columnGap={8}>
              <CustomSelect
                label="Document Type"
                error={errors.documents?.[index]?.documentType}
                required
                placeholder="Select document type"
                {...register(`documents.${index}.documentType`, { required: 'Required' })}
              >
                  {requiredDocuments.map((doc, i) => (
                    <option key={i} value={doc}>{doc}</option>
                  ))}
                  <option value="Other">Other</option>
              </CustomSelect>

              <CustomInput
                label="Document No."
                error={errors.documents?.[index]?.documentNo}
                placeholder="Enter document number"
                {...register(`documents.${index}.documentNo`)}
              />

              <CustomInput
                label="Document Expiry"
                error={errors.documents?.[index]?.expiryDate}
                type="date"
                {...register(`documents.${index}.expiryDate`)}
              />

              <CustomInput
                label="Upload Document"
                error={errors.documents?.[index]?.file}
                required={!hasFile} // Only required if no file is already present/uploaded
                type="file"
                pt={1}
                {...register(`documents.${index}.file`, { 
                    required: !hasFile ? 'File is required' : false 
                })}
              />
              {hasFile && (
                  <Text fontSize="xs" color="green.600" mt={-2}>
                      Uploaded: {fileName || "File attached"}
                  </Text>
              )}
            </SimpleGrid>
          </Box>
        )})}

        <CustomButton
          onClick={() =>
            append({
              documentType: '',
              documentNo: '',
              expiryDate: '',
              file: null,
            })
          }
          variant="outline"
          colorScheme="blue"
        >
          <FaPlus style={{ marginRight: '8px' }} />
          Add Another Document
        </CustomButton>

        <Flex justify="space-between" mt={8}>
          <CustomButton onClick={onBack} variant="ghost" color="gray.600">
            Back
          </CustomButton>
          <CustomButton
            type="submit"
            colorScheme="blue"
            size="lg"
            isLoading={loading}
            loadingText="Saving..."
          >
            Next: Bank Accounts
          </CustomButton>
        </Flex>
      </VStack>
    </form>
  );
};

export default DocumentsVerification;
